/**
 * Predefined shop colors allocated in order
 * Design Rules:
 * 1. Hue difference ≥ 30–40°
 * 2. Similar luminance (prevents one branch dominating)
 * 3. Moderate saturation (no neon, no pastel washout)
 */
const PREDEFINED_SHOP_COLORS = [
  "#3F6AE1", // Blue
  "#00A3C4", // Cyan
  "#16A34A", // Green
  "#DC2626", // Red
  "#C24FA5", // Pink
  "#7A3EE0", // Violet
  "#D65C73", // Rose
  "#D9793D", // Orange
  "#B58A2F", // Gold
  "#7B6F63", // Brown
];

interface HSL {
  h: number; // Hue (0-360)
  s: number; // Saturation (0-100)
  l: number; // Lightness (0-100)
}

/**
 * Convert hex color to HSL
 */
function hexToHSL(hex: string): HSL {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Calculate the minimum hue difference between a new hue and existing hues
 * Accounts for circular nature of hue (0° = 360°)
 */
function getMinHueDifference(newHue: number, existingHues: number[]): number {
  if (existingHues.length === 0) return 360;

  let minDiff = 360;
  for (const existingHue of existingHues) {
    // Calculate difference accounting for circular nature
    const diff1 = Math.abs(newHue - existingHue);
    const diff2 = 360 - diff1;
    const actualDiff = Math.min(diff1, diff2);
    minDiff = Math.min(minDiff, actualDiff);
  }

  return minDiff;
}

/**
 * Generate a new color that is visually distinct from existing colors
 * Following design rules:
 * - Hue difference ≥ 30-40° from all existing colors
 * - Similar luminance (40-60%)
 * - Moderate saturation (50-70%)
 */
function generateDistinctColor(existingColors: string[]): string {
  const existingHSLs = existingColors.map(hexToHSL);
  const existingHues = existingHSLs.map((hsl) => hsl.h);

  // Target luminance and saturation based on predefined colors
  const targetLuminance = 50; // Middle range
  const targetSaturation = 60; // Moderate saturation

  // Try to find a hue with at least 40° difference from all existing colors
  const minHueDiff = 40;
  const maxAttempts = 360 / minHueDiff; // Maximum possible distinct colors

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Start at a random offset and iterate through hues
    const baseHue = Math.floor(Math.random() * minHueDiff);
    const hue = (baseHue + attempt * minHueDiff) % 360;

    const hueDiff = getMinHueDifference(hue, existingHues);

    if (hueDiff >= minHueDiff) {
      // Found a good hue, add some variation to luminance and saturation
      const saturation = targetSaturation + Math.floor(Math.random() * 20) - 10; // ±10%
      const lightness = targetLuminance + Math.floor(Math.random() * 20) - 10; // ±10%

      return hslToHex(hue, saturation, lightness);
    }
  }

  // Fallback: if we can't find a hue with 40° difference, try 30°
  for (let attempt = 0; attempt < 12; attempt++) {
    const hue = Math.floor(Math.random() * 360);
    const hueDiff = getMinHueDifference(hue, existingHues);

    if (hueDiff >= 30) {
      const saturation = targetSaturation + Math.floor(Math.random() * 20) - 10;
      const lightness = targetLuminance + Math.floor(Math.random() * 20) - 10;
      return hslToHex(hue, saturation, lightness);
    }
  }

  // Last resort: generate any color with moderate saturation and luminance
  const hue = Math.floor(Math.random() * 360);
  const saturation = targetSaturation + Math.floor(Math.random() * 20) - 10;
  const lightness = targetLuminance + Math.floor(Math.random() * 20) - 10;
  return hslToHex(hue, saturation, lightness);
}

/**
 * Get the next available shop color
 * - First tries to assign predefined colors in order
 * - If all predefined colors are used, generates a new distinct color
 *
 * @param existingColors - Array of colors already assigned to shops
 * @returns The next color to assign
 */
export function getNextShopColor(existingColors: string[]): string {
  // Find first predefined color not in use
  for (const color of PREDEFINED_SHOP_COLORS) {
    if (
      !existingColors.includes(color.toUpperCase()) &&
      !existingColors.includes(color.toLowerCase())
    ) {
      return color;
    }
  }

  // All predefined colors are used, generate a new distinct color
  return generateDistinctColor(existingColors);
}

/**
 * Convert HSL to Hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
